/**
 * 通用 WebSocket 客户端基类
 *
 * 协议契约 (来自 backend docs/api/legal-websocket-api.md):
 *  - 鉴权: Query 携带 `clientid=<x>&Authorization=Bearer%20<token>` (浏览器不支持自定义 header)
 *  - 上行: { action, ...业务字段平铺 }   不嵌套 data
 *  - 下行: { event, data: {...} }
 *  - 心跳: 客户端 25-30s 发 { action: 'ping' }, 服务端回 { event: 'pong' }
 *  - ID:   后端 Long 可能超 JS Number.MAX_SAFE_INTEGER, 业务层用 string 存储/比较
 *
 * 行为:
 *  1. 自动重连 — 指数退避 (1s → 2s → 4s → 8s → 15s 上限), 最多 5 次
 *  2. 心跳保活 — 25s 发 ping; 60s 未收到 pong 视为掉线 → close → 重连
 *  3. 事件分发 — 通过 event 字符串订阅, 多 listener 并发
 *  4. 离线缓冲 — connect 之前 / 重连期间 send 的消息进 outbox, 重连后 flush
 *  5. 主动关闭 — disconnect() 不会触发重连
 */
import { useAuthStore } from '@/stores/auth'

export interface WsEnvelopeIn {
  event: string
  data?: unknown
}

export type WsListener = (data: unknown) => void

interface WsClientOptions {
  /** 端点 path, 例如 /ws/client/legal/consult/chat */
  path: string
  /** 调试日志前缀 (e.g. 'consult-ws' / 'ai-stream-ws') */
  debugName?: string
  /** 是否启用 ping/pong 心跳 (默认 true); AI 流式端点不支持 ping, 设为 false) */
  heartbeat?: boolean
}

const HEARTBEAT_INTERVAL_MS = 25_000
const HEARTBEAT_TIMEOUT_MS = 60_000
const MAX_RETRY = 5
const RETRY_BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 15_000] as const

export class WsClient {
  private socket: WebSocket | null = null
  private readonly path: string
  private readonly debugName: string
  private readonly heartbeatEnabled: boolean
  private readonly listeners = new Map<string, Set<WsListener>>()
  private outbox: object[] = []
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private heartbeatTimeoutTimer: ReturnType<typeof setTimeout> | null = null
  private retryCount = 0
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private manualClose = false

  /** 上次见到的 token, 用于检测 null → value 的跃迁 */
  private lastSeenToken: string | null = null
  /** 是否有页面调过 connect() 但因无 token 而被搁置 */
  private connectRequested = false

  constructor(opts: WsClientOptions) {
    this.path = opts.path
    this.debugName = opts.debugName ?? 'ws'
    this.heartbeatEnabled = opts.heartbeat ?? true

    // 订阅 auth store, 登录拿到 token 后自动补连;
    // 登出 token 清除时主动断开 socket.
    this.lastSeenToken = useAuthStore.getState().accessToken
    useAuthStore.subscribe((state) => {
      const token = state.accessToken
      if (token === this.lastSeenToken) return
      const prev = this.lastSeenToken
      this.lastSeenToken = token
      if (!prev && token && this.connectRequested && !this.manualClose) {
        // null → value: 之前因无 token 搁置, 现在补连
        this.connect()
      } else if (prev && !token) {
        // value → null: 登出, 关闭现有连接
        this.disconnect()
      }
    })
  }

  /** 当前连接状态 */
  get readyState(): number {
    return this.socket?.readyState ?? WebSocket.CLOSED
  }

  /** 是否已开启可发送 */
  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN
  }

  /** 打开连接 (重复调用幂等; 无 token 时静默挂起, 待登录后自动补连) */
  connect(): void {
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return
    }
    this.manualClose = false
    this.connectRequested = true
    const url = this.buildUrl()
    if (!url) {
      // 未登录 — 不报警, 不连; auth store 监听器会在拿到 token 后自动补连
      return
    }
    try {
      this.socket = new WebSocket(url)
    } catch (err) {
      console.warn(`[${this.debugName}] socket url error`, err)
      this.socket = null
      return
    }
    this.socket.onopen = () => this.handleOpen()
    this.socket.onclose = () => this.handleClose()
    this.socket.onerror = () => this.handleError()
    this.socket.onmessage = (e) => this.handleMessage(e.data)
  }

  /** 主动关闭, 清空重连定时 */
  disconnect(): void {
    this.manualClose = true
    this.connectRequested = false
    this.clearTimers()
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    this.socket?.close()
    this.socket = null
  }

  /** 订阅某 event, 返回 unsubscribe */
  on<T = unknown>(event: string, listener: (data: T) => void): () => void {
    let set = this.listeners.get(event)
    if (!set) {
      set = new Set()
      this.listeners.set(event, set)
    }
    const wrapper = listener as WsListener
    set.add(wrapper)
    return () => set!.delete(wrapper)
  }

  /** 发送上行 — 离线则缓冲 */
  send(payload: object): void {
    if (this.isOpen) {
      this.socket!.send(JSON.stringify(payload))
    } else {
      this.outbox.push(payload)
      // 还没连过? 自动 connect
      if (!this.socket && !this.manualClose) this.connect()
    }
  }

  /* ---------- 内部 ---------- */

  private buildUrl(): string | null {
    const token = useAuthStore.getState().accessToken
    if (!token) return null
    const authToken = normalizeWsAuthToken(token)
    if (!authToken) {
      return null
    }
    const clientId = import.meta.env.VITE_AUTH_CLIENT_ID as string | undefined
    const configuredBase = import.meta.env.VITE_WS_BASE_URL?.trim()
    const base = configuredBase ? resolveWsBaseUrl(configuredBase) : this.sameOriginBase()
    const auth = `Authorization=${encodeURIComponent(`Bearer ${authToken}`)}`
    const cid = clientId ? `&clientid=${encodeURIComponent(clientId)}` : ''
    return `${base}${this.path}?${auth}${cid}`
  }

  private sameOriginBase(): string {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${proto}//${window.location.host}`
  }

  private handleOpen() {
    this.retryCount = 0
    if (this.heartbeatEnabled) this.startHeartbeat()
    while (this.outbox.length > 0) {
      const payload = this.outbox.shift()!
      this.socket?.send(JSON.stringify(payload))
    }
  }

  private handleClose() {
    this.clearTimers()
    this.socket = null
    if (!this.manualClose) this.scheduleRetry()
  }

  private handleError() {
    console.warn(`[${this.debugName}] socket error`)
  }

  private handleMessage(raw: unknown) {
    if (typeof raw !== 'string') return
    let env: WsEnvelopeIn
    try {
      env = JSON.parse(raw) as WsEnvelopeIn
    } catch {
      console.warn(`[${this.debugName}] non-JSON frame`, raw)
      return
    }
    if (env.event === 'pong') {
      this.resetHeartbeatTimeout()
      return
    }
    const set = this.listeners.get(env.event)
    set?.forEach((l) => {
      try {
        l(env.data)
      } catch (e) {
        console.warn(`[${this.debugName}] listener throw`, e)
      }
    })
  }

  private startHeartbeat() {
    this.clearTimers()
    this.heartbeatTimer = setInterval(() => {
      this.send({ action: 'ping' })
      this.resetHeartbeatTimeout()
    }, HEARTBEAT_INTERVAL_MS)
    this.resetHeartbeatTimeout()
  }

  private resetHeartbeatTimeout() {
    if (this.heartbeatTimeoutTimer) clearTimeout(this.heartbeatTimeoutTimer)
    this.heartbeatTimeoutTimer = setTimeout(() => {
      console.warn(`[${this.debugName}] heartbeat timeout, reconnecting`)
      this.socket?.close()
    }, HEARTBEAT_TIMEOUT_MS)
  }

  private clearTimers() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    if (this.heartbeatTimeoutTimer) clearTimeout(this.heartbeatTimeoutTimer)
    this.heartbeatTimer = null
    this.heartbeatTimeoutTimer = null
  }

  private scheduleRetry() {
    if (this.retryCount >= MAX_RETRY) {
      console.warn(`[${this.debugName}] retry budget exhausted, give up`)
      return
    }
    const delay = RETRY_BACKOFF_MS[this.retryCount] ?? RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1]
    this.retryCount += 1
    this.retryTimer = setTimeout(() => this.connect(), delay)
  }
}

function normalizeWsAuthToken(token: string): string | null {
  const cleaned = token
    .trim()
    .replace(/^Bearer\s+/i, '')
    .trim()

  if (!cleaned || hasUrlControlChars(cleaned)) return null
  return cleaned
}

function hasUrlControlChars(value: string): boolean {
  return Array.from(value).some((char) => {
    const code = char.charCodeAt(0)
    return code <= 31 || code === 127
  })
}

function resolveWsBaseUrl(rawBase: string): string {
  const base = rawBase.replace(/\/+$/, '')
  if (base.startsWith('https://')) {
    return `wss://${base.slice('https://'.length)}`
  }
  if (base.startsWith('http://')) {
    return `ws://${base.slice('http://'.length)}`
  }
  return base
}
